/**
 * Institution Provisioning Wizard — onboarding integral guiado.
 *
 * Reemplaza el flujo manual (crear -> ir a detalle -> asignar plan ->
 * aprovisionar) por un wizard de 5 pasos que ejecuta todo en una unica
 * transaccion atomica en el backend. Si falla cualquier paso, no queda
 * IPS a medias.
 */

import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Building2,
  UserPlus,
  CreditCard,
  Layers,
  CheckCircle2,
  ArrowLeft,
  ArrowRight,
  Loader2,
  Copy,
  AlertCircle,
} from 'lucide-react'
import { Button } from '@/presentation/components/ui/Button'
import { Input } from '@/presentation/components/ui/Input'
import { Card, CardContent, CardHeader, CardTitle } from '@/presentation/components/ui/Card'
import { useGlobalCatalog } from '@/application/institutions'
import { usePlans } from '@/application/subscriptions/useSubscriptions'
import { useProvisionInstitution } from '@/application/admin-provisioning/useAdminProvisioning'
import type { ProvisionInstitutionOutput } from '@/application/admin-provisioning/admin-provisioning.service'
import { APP_ROUTES } from '@/config/constants'
import { toast } from '@/lib/toast'
import { getErrorMessage } from '@/lib/api'

const STEPS = [
  { key: 'institution', label: 'Institucion', icon: Building2 },
  { key: 'admin', label: 'Administrador', icon: UserPlus },
  { key: 'plan', label: 'Plan', icon: CreditCard },
  { key: 'catalog', label: 'Catalogo', icon: Layers },
  { key: 'review', label: 'Revisar y crear', icon: CheckCircle2 },
] as const

type StepKey = (typeof STEPS)[number]['key']

interface FormState {
  // Paso 1
  name: string
  slug: string
  nit: string
  address: string
  city: string
  phone: string
  email: string
  // Paso 2
  adminEmail: string
  adminFirstName: string
  adminLastName: string
  // Paso 3
  planCode: string
  trialDaysOverride: string
  subscriptionNotes: string
  // Paso 4
  studyTypeCodes: Set<string>
  priorityCodes: Set<string>
}

const INITIAL_STATE: FormState = {
  name: '',
  slug: '',
  nit: '',
  address: '',
  city: '',
  phone: '',
  email: '',
  adminEmail: '',
  adminFirstName: '',
  adminLastName: '',
  planCode: '',
  trialDaysOverride: '',
  subscriptionNotes: '',
  studyTypeCodes: new Set(),
  priorityCodes: new Set(),
}

const slugify = (value: string): string =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50)

export default function InstitutionProvisioningPage() {
  const navigate = useNavigate()
  const [stepIndex, setStepIndex] = useState(0)
  const [form, setForm] = useState<FormState>(INITIAL_STATE)
  const [result, setResult] = useState<ProvisionInstitutionOutput | null>(null)

  const plansQuery = usePlans()
  const catalogQuery = useGlobalCatalog(true)
  const provisionMutation = useProvisionInstitution()

  const currentStep = STEPS[stepIndex]?.key ?? 'institution'

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  const updateName = (name: string) => {
    setForm((prev) => ({
      ...prev,
      name,
      // Auto-generar slug solo si el slug aun no fue tocado manualmente
      slug: prev.slug && prev.slug !== slugify(prev.name) ? prev.slug : slugify(name),
    }))
  }

  const validation = useMemo(() => validateStep(currentStep, form), [currentStep, form])
  const canAdvance = validation.valid

  const goNext = () => {
    if (stepIndex < STEPS.length - 1) setStepIndex(stepIndex + 1)
  }
  const goBack = () => {
    if (stepIndex > 0) setStepIndex(stepIndex - 1)
  }

  const handleSubmit = () => {
    provisionMutation.mutate(
      {
        name: form.name.trim(),
        slug: form.slug.trim(),
        nit: form.nit.trim() || undefined,
        address: form.address.trim() || undefined,
        city: form.city.trim() || undefined,
        phone: form.phone.trim() || undefined,
        email: form.email.trim() || undefined,
        adminEmail: form.adminEmail.trim(),
        adminFirstName: form.adminFirstName.trim() || undefined,
        adminLastName: form.adminLastName.trim() || undefined,
        planCode: form.planCode,
        trialDaysOverride: form.trialDaysOverride
          ? Number(form.trialDaysOverride)
          : undefined,
        subscriptionNotes: form.subscriptionNotes.trim() || undefined,
        studyTypeCodes: Array.from(form.studyTypeCodes),
        priorityCodes: Array.from(form.priorityCodes),
      },
      {
        onSuccess: (data) => {
          setResult(data)
          toast.success('Institucion creada exitosamente')
        },
        onError: (err) => toast.error(getErrorMessage(err)),
      }
    )
  }

  // Pantalla de exito post-creacion
  if (result) {
    return (
      <SuccessScreen
        result={result}
        onGoToDetail={() =>
          navigate(APP_ROUTES.SUPER_ADMIN.INSTITUTION_DETAIL(result.institution.id))
        }
        onCreateAnother={() => {
          setResult(null)
          setForm(INITIAL_STATE)
          setStepIndex(0)
        }}
      />
    )
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Cancelar
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Aprovisionar institucion</h1>
          <p className="text-[var(--foreground-muted)] text-sm">
            Crea la IPS, el admin inicial, asigna plan y aprovisiona insumos en un solo paso.
          </p>
        </div>
      </div>

      {/* Stepper */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center justify-between gap-2">
            {STEPS.map((step, i) => {
              const isActive = i === stepIndex
              const isComplete = i < stepIndex
              const Icon = step.icon
              return (
                <div key={step.key} className="flex items-center flex-1 last:flex-none">
                  <div className="flex flex-col items-center gap-1">
                    <div
                      className={`flex items-center justify-center h-9 w-9 rounded-full border-2 ${
                        isActive
                          ? 'bg-[var(--primary)] text-white border-[var(--primary)]'
                          : isComplete
                            ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 border-green-600'
                            : 'bg-white text-[var(--foreground-muted)] border-[var(--border)]'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                    </div>
                    <span
                      className={`text-xs font-medium ${
                        isActive ? 'text-[var(--primary)]' : 'text-[var(--foreground-muted)]'
                      }`}
                    >
                      {step.label}
                    </span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div
                      className={`flex-1 h-0.5 mx-2 ${
                        isComplete ? 'bg-green-500' : 'bg-[var(--border)]'
                      }`}
                    />
                  )}
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>

      {/* Step content */}
      <Card>
        <CardHeader>
          <CardTitle>{STEPS[stepIndex]?.label}</CardTitle>
        </CardHeader>
        <CardContent>
          {currentStep === 'institution' && (
            <InstitutionStep form={form} update={update} updateName={updateName} />
          )}
          {currentStep === 'admin' && <AdminStep form={form} update={update} />}
          {currentStep === 'plan' && (
            <PlanStep
              form={form}
              update={update}
              plans={plansQuery.data?.plans ?? []}
              loading={plansQuery.isLoading}
            />
          )}
          {currentStep === 'catalog' && (
            <CatalogStep
              form={form}
              update={update}
              catalog={catalogQuery.data}
              loading={catalogQuery.isLoading}
            />
          )}
          {currentStep === 'review' && <ReviewStep form={form} />}

          {validation.errors.length > 0 && (
            <div className="mt-4 rounded-md bg-yellow-50 dark:bg-yellow-950/40 border border-yellow-200 dark:border-yellow-800 p-3 text-sm text-yellow-900 dark:text-yellow-100">
              <p className="font-semibold mb-1 flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                Datos requeridos:
              </p>
              <ul className="list-disc pl-5 space-y-0.5">
                {validation.errors.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Footer nav */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={goBack} disabled={stepIndex === 0}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Anterior
        </Button>
        {currentStep === 'review' ? (
          <Button onClick={handleSubmit} disabled={provisionMutation.isPending || !canAdvance}>
            {provisionMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creando...
              </>
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Crear institucion
              </>
            )}
          </Button>
        ) : (
          <Button onClick={goNext} disabled={!canAdvance}>
            Siguiente
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        )}
      </div>
    </div>
  )
}

// =============================================================================
// Validacion por paso
// =============================================================================

function validateStep(step: StepKey, form: FormState): { valid: boolean; errors: string[] } {
  const errors: string[] = []
  if (step === 'institution') {
    if (form.name.trim().length < 2) errors.push('Nombre de la institucion (mín. 2 caracteres)')
    if (!/^[a-z0-9-]{2,50}$/.test(form.slug)) {
      errors.push('Slug: 2-50 caracteres en minusculas, numeros y guiones')
    }
  } else if (step === 'admin') {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.adminEmail)) {
      errors.push('Email del administrador invalido')
    }
  } else if (step === 'plan') {
    if (!form.planCode) errors.push('Selecciona un plan')
    if (form.trialDaysOverride && Number(form.trialDaysOverride) < 0) {
      errors.push('Dias de trial no puede ser negativo')
    }
  } else if (step === 'review') {
    if (!form.name || !form.slug || !form.adminEmail || !form.planCode) {
      errors.push('Completa todos los pasos antes de crear')
    }
  }
  return { valid: errors.length === 0, errors }
}

// =============================================================================
// Subcomponentes
// =============================================================================

function InstitutionStep({
  form,
  update,
  updateName,
}: {
  form: FormState
  update: <K extends keyof FormState>(key: K, value: FormState[K]) => void
  updateName: (name: string) => void
}) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="md:col-span-2">
        <label className="text-sm font-medium">Nombre *</label>
        <Input
          value={form.name}
          onChange={(e) => updateName(e.target.value)}
          placeholder="Ej. Clinica San Rafael"
        />
      </div>
      <div>
        <label className="text-sm font-medium">Slug (URL) *</label>
        <Input
          value={form.slug}
          onChange={(e) => update('slug', slugify(e.target.value))}
          placeholder="clinica-san-rafael"
        />
        <p className="text-xs text-[var(--foreground-muted)] mt-1">
          Auto-generado desde el nombre. Se puede editar.
        </p>
      </div>
      <div>
        <label className="text-sm font-medium">NIT</label>
        <Input
          value={form.nit}
          onChange={(e) => update('nit', e.target.value)}
          placeholder="900123456-7"
        />
      </div>
      <div>
        <label className="text-sm font-medium">Ciudad</label>
        <Input
          value={form.city}
          onChange={(e) => update('city', e.target.value)}
          placeholder="Bogota"
        />
      </div>
      <div>
        <label className="text-sm font-medium">Telefono</label>
        <Input
          value={form.phone}
          onChange={(e) => update('phone', e.target.value)}
          placeholder="+57 1 234 5678"
        />
      </div>
      <div className="md:col-span-2">
        <label className="text-sm font-medium">Direccion</label>
        <Input
          value={form.address}
          onChange={(e) => update('address', e.target.value)}
          placeholder="Calle 100 # 15-20"
        />
      </div>
      <div className="md:col-span-2">
        <label className="text-sm font-medium">Email institucional</label>
        <Input
          type="email"
          value={form.email}
          onChange={(e) => update('email', e.target.value)}
          placeholder="contacto@clinica.com"
        />
      </div>
    </div>
  )
}

function AdminStep({
  form,
  update,
}: {
  form: FormState
  update: <K extends keyof FormState>(key: K, value: FormState[K]) => void
}) {
  return (
    <div className="space-y-4">
      <div className="rounded-md bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 p-3 text-sm text-blue-900 dark:text-blue-100">
        <p>
          Se creara un usuario ADMIN para esta IPS con una contrasena temporal
          que se mostrara una sola vez al final del proceso. El admin debera
          cambiarla en su primer login.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="text-sm font-medium">Email del admin *</label>
          <Input
            type="email"
            value={form.adminEmail}
            onChange={(e) => update('adminEmail', e.target.value)}
            placeholder="admin@clinica.com"
          />
        </div>
        <div>
          <label className="text-sm font-medium">Nombre</label>
          <Input
            value={form.adminFirstName}
            onChange={(e) => update('adminFirstName', e.target.value)}
            placeholder="Juan"
          />
        </div>
        <div>
          <label className="text-sm font-medium">Apellido</label>
          <Input
            value={form.adminLastName}
            onChange={(e) => update('adminLastName', e.target.value)}
            placeholder="Perez"
          />
        </div>
      </div>
    </div>
  )
}

function PlanStep({
  form,
  update,
  plans,
  loading,
}: {
  form: FormState
  update: <K extends keyof FormState>(key: K, value: FormState[K]) => void
  plans: Array<{ code: string; name: string; isPilot: boolean; trialDays: number }>
  loading: boolean
}) {
  if (loading) return <Loader2 className="h-6 w-6 animate-spin text-[var(--primary)]" />

  return (
    <div className="space-y-4">
      <div>
        <label className="text-sm font-medium block mb-2">Selecciona un plan *</label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {plans.map((p) => {
            const isSelected = form.planCode === p.code
            return (
              <button
                key={p.code}
                type="button"
                onClick={() => update('planCode', p.code)}
                className={`text-left p-4 rounded-lg border-2 transition-all ${
                  isSelected
                    ? 'border-[var(--primary)] bg-blue-50 dark:bg-blue-950/40'
                    : 'border-[var(--border)] hover:border-[var(--primary)]'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold">{p.name}</p>
                    <p className="text-xs text-[var(--foreground-muted)]">{p.code}</p>
                  </div>
                  {p.isPilot && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200">
                      Trial {p.trialDays}d
                    </span>
                  )}
                </div>
              </button>
            )
          })}
        </div>
      </div>

      <div>
        <label className="text-sm font-medium">Override de dias de trial (opcional)</label>
        <Input
          type="number"
          min={0}
          max={365}
          value={form.trialDaysOverride}
          onChange={(e) => update('trialDaysOverride', e.target.value)}
          placeholder="Usa el default del plan"
        />
        <p className="text-xs text-[var(--foreground-muted)] mt-1">
          Solo aplica si el plan es de tipo PILOTO. Negociaciones individuales.
        </p>
      </div>

      <div>
        <label className="text-sm font-medium">Notas (opcional)</label>
        <Input
          value={form.subscriptionNotes}
          onChange={(e) => update('subscriptionNotes', e.target.value)}
          placeholder="Razon del trial extendido, contacto comercial..."
        />
      </div>
    </div>
  )
}

interface CatalogData {
  studyTypes: Array<{ code: string; name: string }>
  priorities: Array<{ code: string; name: string }>
}

function CatalogStep({
  form,
  update,
  catalog,
  loading,
}: {
  form: FormState
  update: <K extends keyof FormState>(key: K, value: FormState[K]) => void
  catalog?: CatalogData
  loading: boolean
}) {
  if (loading) return <Loader2 className="h-6 w-6 animate-spin text-[var(--primary)]" />
  if (!catalog) return null

  const toggleAll = (kind: 'studyTypes' | 'priorities', selectAll: boolean) => {
    if (kind === 'studyTypes') {
      update(
        'studyTypeCodes',
        selectAll ? new Set(catalog.studyTypes.map((s) => s.code)) : new Set()
      )
    } else {
      update(
        'priorityCodes',
        selectAll ? new Set(catalog.priorities.map((p) => p.code)) : new Set()
      )
    }
  }

  return (
    <div className="space-y-6">
      <div className="rounded-md bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 p-3 text-sm text-blue-900 dark:text-blue-100">
        <p>
          Selecciona los insumos del catalogo global que quieres aprovisionar a
          esta IPS. Es opcional — la IPS tambien puede crearlos despues de
          forma individual.
        </p>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold">
            Tipos de estudio ({form.studyTypeCodes.size}/{catalog.studyTypes.length})
          </h3>
          <div className="flex gap-2 text-xs">
            <button
              type="button"
              className="text-[var(--primary)]"
              onClick={() => toggleAll('studyTypes', true)}
            >
              Seleccionar todos
            </button>
            <button
              type="button"
              className="text-[var(--foreground-muted)]"
              onClick={() => toggleAll('studyTypes', false)}
            >
              Ninguno
            </button>
          </div>
        </div>
        <div className="space-y-1 max-h-40 overflow-y-auto border border-[var(--border)] rounded-md p-2">
          {catalog.studyTypes.map((s) => (
            <label
              key={s.code}
              className="flex items-center gap-2 p-1 rounded hover:bg-[var(--background-muted)] cursor-pointer text-sm"
            >
              <input
                type="checkbox"
                checked={form.studyTypeCodes.has(s.code)}
                onChange={(e) => {
                  const next = new Set(form.studyTypeCodes)
                  if (e.target.checked) next.add(s.code)
                  else next.delete(s.code)
                  update('studyTypeCodes', next)
                }}
              />
              <span className="font-mono text-xs">{s.code}</span>
              <span>{s.name}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold">
            Prioridades ({form.priorityCodes.size}/{catalog.priorities.length})
          </h3>
          <div className="flex gap-2 text-xs">
            <button
              type="button"
              className="text-[var(--primary)]"
              onClick={() => toggleAll('priorities', true)}
            >
              Seleccionar todas
            </button>
            <button
              type="button"
              className="text-[var(--foreground-muted)]"
              onClick={() => toggleAll('priorities', false)}
            >
              Ninguna
            </button>
          </div>
        </div>
        <div className="space-y-1 max-h-40 overflow-y-auto border border-[var(--border)] rounded-md p-2">
          {catalog.priorities.map((p) => (
            <label
              key={p.code}
              className="flex items-center gap-2 p-1 rounded hover:bg-[var(--background-muted)] cursor-pointer text-sm"
            >
              <input
                type="checkbox"
                checked={form.priorityCodes.has(p.code)}
                onChange={(e) => {
                  const next = new Set(form.priorityCodes)
                  if (e.target.checked) next.add(p.code)
                  else next.delete(p.code)
                  update('priorityCodes', next)
                }}
              />
              <span className="font-mono text-xs">{p.code}</span>
              <span>{p.name}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  )
}

function ReviewStep({ form }: { form: FormState }) {
  return (
    <div className="space-y-4 text-sm">
      <ReviewSection title="Institucion">
        <ReviewField label="Nombre" value={form.name} />
        <ReviewField label="Slug" value={form.slug} mono />
        <ReviewField label="NIT" value={form.nit} />
        <ReviewField label="Ciudad" value={form.city} />
        <ReviewField label="Telefono" value={form.phone} />
        <ReviewField label="Email" value={form.email} />
      </ReviewSection>
      <ReviewSection title="Administrador inicial">
        <ReviewField label="Email" value={form.adminEmail} />
        <ReviewField
          label="Nombre"
          value={[form.adminFirstName, form.adminLastName].filter(Boolean).join(' ')}
        />
      </ReviewSection>
      <ReviewSection title="Plan">
        <ReviewField label="Codigo" value={form.planCode} />
        {form.trialDaysOverride && (
          <ReviewField label="Trial override" value={`${form.trialDaysOverride} dias`} />
        )}
      </ReviewSection>
      <ReviewSection title="Catalogo a aprovisionar">
        <ReviewField label="Tipos de estudio" value={String(form.studyTypeCodes.size)} />
        <ReviewField label="Prioridades" value={String(form.priorityCodes.size)} />
      </ReviewSection>
    </div>
  )
}

function ReviewSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-xs uppercase tracking-wide font-semibold text-[var(--foreground-muted)] mb-1">
        {title}
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 rounded-md border border-[var(--border)] p-3">
        {children}
      </div>
    </div>
  )
}

function ReviewField({
  label,
  value,
  mono,
}: {
  label: string
  value: string
  mono?: boolean
}) {
  return (
    <div>
      <span className="text-[var(--foreground-muted)] text-xs">{label}</span>
      <div className={mono ? 'font-mono' : ''}>{value || '—'}</div>
    </div>
  )
}

// =============================================================================
// Pantalla de exito
// =============================================================================

function SuccessScreen({
  result,
  onGoToDetail,
  onCreateAnother,
}: {
  result: ProvisionInstitutionOutput
  onGoToDetail: () => void
  onCreateAnother: () => void
}) {
  const copyPassword = () => {
    void navigator.clipboard.writeText(result.adminUser.temporaryPassword)
    toast.success('Contrasena copiada')
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="text-center space-y-2">
        <CheckCircle2 className="h-16 w-16 text-green-600 dark:text-green-400 mx-auto" />
        <h1 className="text-2xl font-bold tracking-tight">Institucion creada</h1>
        <p className="text-[var(--foreground-muted)]">
          {result.institution.name} esta lista para operar.
        </p>
      </div>

      <Card className="border-yellow-300 bg-yellow-50 dark:bg-yellow-950/40">
        <CardHeader>
          <CardTitle className="text-yellow-900 dark:text-yellow-100 flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            Contrasena temporal del admin
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-yellow-900 dark:text-yellow-100">
            Esta contrasena se muestra UNA SOLA VEZ. Guardala y compartesela al
            administrador de la IPS por un canal seguro.
          </p>
          <div className="flex gap-2">
            <Input
              readOnly
              value={result.adminUser.temporaryPassword}
              className="font-mono text-sm"
            />
            <Button variant="outline" onClick={copyPassword}>
              <Copy className="h-4 w-4 mr-2" />
              Copiar
            </Button>
          </div>
          <p className="text-xs text-yellow-900 dark:text-yellow-100">
            Email del admin: <strong>{result.adminUser.email}</strong>
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Resumen</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-[var(--foreground-muted)] text-xs">Plan</dt>
              <dd>
                {result.subscription.planCode} ·{' '}
                <span className="font-medium">{result.subscription.status}</span>
              </dd>
            </div>
            {result.subscription.trialEndsAt && (
              <div>
                <dt className="text-[var(--foreground-muted)] text-xs">Fin de trial</dt>
                <dd>
                  {new Date(result.subscription.trialEndsAt).toLocaleDateString('es-CO')}
                </dd>
              </div>
            )}
            {/*
              Se dice "propios" porque el numero cuenta SOLO lo aprovisionado
              a esta IPS. Quien daba de alta un cliente leia "Tipos de estudio
              aprovisionados: 0" y creia haberlo dejado sin catalogo, cuando
              la institucion abre viendo el catalogo global.
            */}
            <div>
              <dt className="text-[var(--foreground-muted)] text-xs">
                Tipos de estudio propios
              </dt>
              <dd>{result.provisioning.studyTypesCopied}</dd>
            </div>
            <div>
              <dt className="text-[var(--foreground-muted)] text-xs">
                Prioridades propias
              </dt>
              <dd>{result.provisioning.prioritiesCopied}</dd>
            </div>
            <div className="sm:col-span-2">
              <dd className="text-xs text-[var(--foreground-muted)]">
                Ademas de estos, la institucion ve el catalogo global de tipos
                de estudio y prioridades sin necesidad de aprovisionar nada.
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      <div className="flex gap-2 justify-center">
        <Button variant="outline" onClick={onCreateAnother}>
          Crear otra
        </Button>
        <Button onClick={onGoToDetail}>
          Ir al detalle
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  )
}
